import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { useOnboardingSettings, AdAccountSettings } from "@/hooks/useOnboardingSettings";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Save, AlertTriangle } from "lucide-react";
import { SetupAdProfiles } from "@/components/setup/SetupAdProfiles";
import { SetupTracking } from "@/components/setup/SetupTracking";
import { SetupNamingConvention } from "@/components/setup/SetupNamingConvention";
import { SetupLaunchDefaults } from "@/components/setup/SetupLaunchDefaults";

type FormState = {
  default_fb_page_id: string | null;
  default_fb_page_name: string | null;
  default_ig_account_id: string | null;
  default_ig_account_name: string | null;
  default_pixel_id: string | null;
  default_pixel_name: string | null;
  default_app_id: string | null;
  default_app_name: string | null;
  website_events_enabled: boolean;
  app_events_enabled: boolean;
  offline_events_enabled: boolean;
  crm_events_enabled: boolean;
  post_engagements_enabled: boolean;
  page_engagements_enabled: boolean;
  default_utm_parameters: string;
  naming_convention: { tags: string[]; separator: string; custom_tags?: string[] };
  multi_advertiser_opt_in: boolean;
  default_ad_status: string;
};

const DEFAULT_FORM: FormState = {
  default_fb_page_id: null,
  default_fb_page_name: null,
  default_ig_account_id: null,
  default_ig_account_name: null,
  default_pixel_id: null,
  default_pixel_name: null,
  default_app_id: null,
  default_app_name: null,
  website_events_enabled: true,
  app_events_enabled: false,
  offline_events_enabled: false,
  crm_events_enabled: false,
  post_engagements_enabled: false,
  page_engagements_enabled: false,
  default_utm_parameters: "",
  naming_convention: { tags: [], separator: "_", custom_tags: [] },
  multi_advertiser_opt_in: true,
  default_ad_status: "PAUSED",
};

function settingsToForm(s: AdAccountSettings): FormState {
  return {
    default_fb_page_id: s.default_fb_page_id,
    default_fb_page_name: s.default_fb_page_name,
    default_ig_account_id: s.default_ig_account_id,
    default_ig_account_name: s.default_ig_account_name,
    default_pixel_id: s.default_pixel_id,
    default_pixel_name: s.default_pixel_name,
    default_app_id: s.default_app_id,
    default_app_name: s.default_app_name,
    website_events_enabled: s.website_events_enabled ?? true,
    app_events_enabled: s.app_events_enabled ?? false,
    offline_events_enabled: s.offline_events_enabled ?? false,
    crm_events_enabled: s.crm_events_enabled ?? false,
    post_engagements_enabled: (s as any).post_engagements_enabled ?? false,
    page_engagements_enabled: (s as any).page_engagements_enabled ?? false,
    default_utm_parameters: (s as any).default_utm_parameters ?? "",
    naming_convention: s.naming_convention ?? { tags: [], separator: "_", custom_tags: [] },
    multi_advertiser_opt_in: s.multi_advertiser_opt_in ?? true,
    default_ad_status: s.default_ad_status ?? "PAUSED",
  };
}

export default function SetupLaunchSettings() {
  const { selectedAdAccountId, selectedAdAccountName, adAccounts, isLoading: accountsLoading } = useAdAccount();
  const { toast } = useToast();

  const { settings, isLoading: settingsLoading, saveStep, isSaving } = useOnboardingSettings(selectedAdAccountId ?? undefined);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [isDirty, setIsDirty] = useState(false);
  const savedSnapshot = useRef<string>("");

  // Load settings into form
  useEffect(() => {
    if (settings) {
      const f = settingsToForm(settings);
      setForm(f);
      savedSnapshot.current = JSON.stringify(f);
      setIsDirty(false);
    } else if (!settingsLoading && selectedAdAccountId) {
      setForm(DEFAULT_FORM);
      savedSnapshot.current = JSON.stringify(DEFAULT_FORM);
      setIsDirty(false);
    }
  }, [settings, settingsLoading, selectedAdAccountId]);

  const updateForm = useCallback((partial: Partial<FormState>) => {
    setForm((prev) => {
      const next = { ...prev, ...partial };
      setIsDirty(JSON.stringify(next) !== savedSnapshot.current);
      return next;
    });
  }, []);

  // Warn on browser/tab close with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleSave = async () => {
    if (!selectedAdAccountId) return;
    try {
      await saveStep(form as any);
      savedSnapshot.current = JSON.stringify(form);
      setIsDirty(false);
      toast({ title: "Settings saved successfully" });
    } catch (err: unknown) {
      toast({ title: "Failed to save settings", description: parseErrorMessage(err), variant: "destructive" });
    }
  };

  // No ad accounts
  if (!accountsLoading && adAccounts.length === 0) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="py-16 text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto" />
              <h3 className="text-lg font-semibold">No ad accounts connected</h3>
              <p className="text-sm text-muted-foreground">
                Go to Settings to connect your Meta account.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isLoading = accountsLoading || settingsLoading;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Launch Settings</h1>
          {selectedAdAccountName && (
            <p className="text-sm text-muted-foreground mt-1">
              {selectedAdAccountName}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48 w-full rounded-lg" />
            ))}
          </div>
        ) : selectedAdAccountId ? (
          <>
            <div className="space-y-6">
              <SetupAdProfiles
                adAccountId={selectedAdAccountId}
                data={{
                  default_fb_page_id: form.default_fb_page_id,
                  default_fb_page_name: form.default_fb_page_name,
                  default_ig_account_id: form.default_ig_account_id,
                  default_ig_account_name: form.default_ig_account_name,
                }}
                onChange={updateForm}
              />

              <SetupTracking
                adAccountId={selectedAdAccountId}
                data={{
                  default_pixel_id: form.default_pixel_id,
                  default_pixel_name: form.default_pixel_name,
                  default_app_id: form.default_app_id,
                  default_app_name: form.default_app_name,
                  website_events_enabled: form.website_events_enabled,
                  app_events_enabled: form.app_events_enabled,
                  offline_events_enabled: form.offline_events_enabled,
                  crm_events_enabled: form.crm_events_enabled,
                  post_engagements_enabled: form.post_engagements_enabled,
                  page_engagements_enabled: form.page_engagements_enabled,
                  default_utm_parameters: form.default_utm_parameters,
                }}
                onChange={updateForm}
              />

              <SetupNamingConvention
                data={{ naming_convention: form.naming_convention }}
                onChange={(d) => updateForm({ naming_convention: d.naming_convention })}
              />

              <SetupLaunchDefaults
                data={{
                  multi_advertiser_opt_in: form.multi_advertiser_opt_in,
                  default_ad_status: form.default_ad_status,
                }}
                onChange={updateForm}
              />
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="w-full"
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : isDirty ? "Save Changes" : "Settings Saved"}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
